import React, { createContext, useContext, useState, useEffect } from 'react';
import { graphqlService } from '../services/graphqlService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const savedUser = localStorage.getItem('truMeritTrainingUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        // Verify session with GraphQL
        if (userData.email) {
          graphqlService.verifyAuth(userData.email)
            .then(result => {
              if (result.success && result.user) {
                const verifiedUser = {
                  ...result.user,
                  designation: result.user.designation || '',
                  specialization: result.user.specialization || ''
                };
                setUser(verifiedUser);
                localStorage.setItem('truMeritTrainingUser', JSON.stringify(verifiedUser));
              } else {
                // Session invalid, clear it
                localStorage.removeItem('truMeritTrainingUser');
                setUser(null);
              }
            })
            .catch(error => {
              console.error('Error verifying auth:', error);
              // If GraphQL fails, use cached user (fallback)
              const migratedUser = {
                ...userData,
                designation: userData.designation || '',
                specialization: userData.specialization || ''
              };
              setUser(migratedUser);
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('truMeritTrainingUser');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    
    try {
      const response = await graphqlService.login(email, password);
      
      if (response.success && response.user) {
        const userData = {
          id: response.user._id || response.user.id,
          email: response.user.email,
          name: response.user.name,
          role: response.user.role,
          department: response.user.department,
          designation: response.user.designation || '',
          specialization: response.user.specialization || '',
          loginTime: new Date().toISOString()
        };
        
        setUser(userData);
        localStorage.setItem('truMeritTrainingUser', JSON.stringify(userData));
        setLoading(false);
        return { success: true, user: userData };
      } else {
        setLoading(false);
        return { success: false, error: response.error || 'Invalid email or password' };
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('truMeritTrainingUser');
    localStorage.removeItem('truMeritTrainingProgress');
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isEmployee = () => {
    return user && user.role === 'employee';
  };

  const hasRole = (role) => {
    return user && user.role === role;
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAdmin,
    isEmployee,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
